import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { DateUtils } from '../../common';
import { EmailUsageDao } from '../daos/email-usage.dao';
import { EnrollmentDao } from '../daos/enrollment.dao';
import { MemberDao } from '../daos/member.dao';
import { UserStatusDao } from '../daos/user-status.dao';
import { IEmailStatus } from '../interfaces/Email';
import { IReservationEnrollment } from '../interfaces/HotelReservation/IReservationEnrollment';
import { Reservation } from '../model/reservation/reservation.model';
import { UserStatusType } from '../model/user/user-status-type.model';
import { User } from '../model/user/user.model';
import { UserModelBuilder } from './builders/user-model.builder';
import { LogService } from './logging.service';
import { UHFInteractionService } from './uhf-interaction.service';

const MEMBER_BUFFER_SIZE = 1;
const MEMBER_DURATION = 10 * 60 * 1000; // 10 mins
const CLASS_NAME = 'MemberService';

@Injectable()
export class MemberService {
  private static readonly NO_OPEN_ACCOUNTS = 'NO.OPEN.ACCOUNT';

  private _memberObservables: Map<string, Observable<User>>;

  constructor(
    private _memberDao: MemberDao,
    private _userStatusDao: UserStatusDao,
    private _uhfInteractionService: UHFInteractionService,
    private _log: LogService,
    private _enrollmentDao: EnrollmentDao,
    private _emailUsageDao: EmailUsageDao) {
    this._memberObservables = new Map<string, Observable<User>>();
  }

  getMember(): Observable<User> {
    return this._getFreshMember();
  }

  isRegisteredEmail(email: string, firstName: string, lastName: string, hotelMnemonic: string, checkInDate: Date, checkOutDate: Date): Observable<boolean> {
    const request: IEmailStatus = {hotelMnemonic, email, firstName, lastName, checkInDate: DateUtils.formatDate(checkInDate), checkOutDate: DateUtils.formatDate(checkOutDate)};
    return this._emailUsageDao.post(request)
      .map((data) => {
        return !(MemberService.NO_OPEN_ACCOUNTS === data.code);
      })
      .catch((error) => {
        this._log.error(`Error when getting email usage status ${email}`, CLASS_NAME, error);
        return Observable.throw(error);
      });
  }

  enroll(reservation: Reservation, simplifiedLoginEnabled): Observable<User> {
    const enrollmentData: IReservationEnrollment = {
      enroll: {
        rewardsClub: true
      },
      ihgConfirmationNumber: reservation.confirmationNumber,
      lastName: reservation.guest.lastName
    };
    return this._enrollmentDao.post(enrollmentData, simplifiedLoginEnabled)
      .map((data) => {
        if (!!data.enroll && data.enroll.temporaryPin) {
          this._uhfInteractionService.logInWithCredentials(data.enroll.memberNumber, data.enroll.temporaryPin, true);
        } else if (!!data.enrollment) {
          this._uhfInteractionService.setToken(data.enrollment);
        }

        return new UserModelBuilder().withStatus(UserStatusType.EXPLICIT).withEnrollmentData(data).build();
      })
      .catch((error) => {
        this._log.error(`Error when enroll with reservation ${reservation.confirmationNumber}`, CLASS_NAME, error);
        return Observable.throw(error);
      });
  }

  private _getFreshMember(): Observable<User> {
    return this._userStatusDao.get()
      .mergeMap((userStatusData: any): Observable<User> => {
        const userStatus = UserStatusType.fromString(userStatusData.status);
        if (UserStatusType.isAnonymous(userStatus)) {
          return Observable.of(new User(userStatus));
        }
        if (UserStatusType.isImplicit(userStatus)) {
          return this._uhfInteractionService.getMemberFromProfile(userStatus);
        }
        return this._memberDao.get()
          .map((memberData: any) => {
            return new UserModelBuilder().withStatus(userStatus).withMemberData(memberData).build();
          })
          .catch((error) => {
            this._log.error(`Error when building member`, CLASS_NAME, error);
            return Observable.throw(error);
          });
      })
      .catch((error) => {
        this._log.error(`Error when getting user information`, CLASS_NAME, error);
        return Observable.throw(error);
      });
  }
}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/api/services/member.service.ts